import { resolvePublicUploadUrl } from "../api/apiConfig/apiConfig";

const IMG_TAG_RE = /\[img\]([\s\S]*?)\[\/img\]/gi;
const HTML_IMG_RE = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
const HTTP_IMAGE_RE =
  /(https?:\/\/[^\s<>"']+\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[^\s<>"']*)?)/gi;

export function escapeTicketMessageHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\n/g, "<br>");
}

export function normalizeTicketImageSrc(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";

  if (/^data:image\//i.test(value)) {
    const comma = value.indexOf(",");
    if (comma === -1) return value;
    const meta = value.slice(0, comma);
    const payload = value.slice(comma + 1).replace(/\s/g, "");
    return `${meta},${payload}`;
  }

  if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) {
    return value;
  }

  if (value.startsWith("/images/") || value.startsWith("images/")) {
    return value.startsWith("/") ? value : `/${value}`;
  }

  // Public S3 keys → absolute URL; private user/ keys need signed URL from API
  return resolvePublicUploadUrl(value) || "";
}

export function isDisplayableTicketImageSrc(url) {
  const value = String(url ?? "").trim();
  if (!value) return false;
  if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) return true;
  if (/^data:image\/(?:png|jpe?g|gif|webp|bmp);base64,/i.test(value)) return true;
  if (/^\/images\//i.test(value)) return true;
  if (/^(?:uploads\/|files\/|attachments\/)/i.test(value)) return true;
  return false;
}

/**
 * @returns {{ type: "text", text: string } | { type: "image", url: string }[]}
 */
export function parseTicketMessage(text) {
  const str = String(text ?? "");
  if (!str.trim()) return [];

  const segments = [];
  let cursor = 0;

  const pushText = (chunk) => {
    if (!chunk) return;
    segments.push({ type: "text", text: chunk });
  };

  const pushImage = (raw) => {
    const url = normalizeTicketImageSrc(raw);
    if (isDisplayableTicketImageSrc(url)) {
      segments.push({ type: "image", url });
    } else {
      pushText(`[img]${raw}[/img]`);
    }
  };

  const tagMatches = [];
  IMG_TAG_RE.lastIndex = 0;
  let tagMatch;
  while ((tagMatch = IMG_TAG_RE.exec(str)) !== null) {
    tagMatches.push({
      index: tagMatch.index,
      end: IMG_TAG_RE.lastIndex,
      raw: tagMatch[1],
    });
  }

  if (!tagMatches.length) {
    const htmlMatches = [];
    HTML_IMG_RE.lastIndex = 0;
    let htmlMatch;
    while ((htmlMatch = HTML_IMG_RE.exec(str)) !== null) {
      htmlMatches.push({
        index: htmlMatch.index,
        end: HTML_IMG_RE.lastIndex,
        raw: htmlMatch[1],
      });
    }

    if (htmlMatches.length) {
      htmlMatches.forEach((match) => {
        if (match.index > cursor) pushText(str.slice(cursor, match.index));
        pushImage(match.raw);
        cursor = match.end;
      });
      if (cursor < str.length) pushText(str.slice(cursor));
      return segments;
    }

    const urlMatches = [];
    HTTP_IMAGE_RE.lastIndex = 0;
    let urlMatch;
    while ((urlMatch = HTTP_IMAGE_RE.exec(str)) !== null) {
      urlMatches.push({
        index: urlMatch.index,
        end: HTTP_IMAGE_RE.lastIndex,
        raw: urlMatch[1],
      });
    }

    if (!urlMatches.length) {
      return [{ type: "text", text: str }];
    }

    urlMatches.forEach((match) => {
      if (match.index > cursor) pushText(str.slice(cursor, match.index));
      pushImage(match.raw);
      cursor = match.end;
    });
    if (cursor < str.length) pushText(str.slice(cursor));
    return segments;
  }

  tagMatches.forEach((match) => {
    if (match.index > cursor) pushText(str.slice(cursor, match.index));
    pushImage(match.raw);
    cursor = match.end;
  });
  if (cursor < str.length) pushText(str.slice(cursor));

  return segments.length ? segments : [{ type: "text", text: str }];
}

export function openTicketImageInNewTab(src) {
  const url = String(src ?? "").trim();
  if (!url) return;

  if (/^data:image\//i.test(url)) {
    try {
      const popup = window.open("", "_blank", "noopener,noreferrer");
      if (popup) {
        const safeSrc = url.replace(/"/g, "&quot;");
        popup.document.write(
          `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Attachment</title></head><body style="margin:0;background:#0f172a;display:flex;align-items:center;justify-content:center;min-height:100vh;"><img src="${safeSrc}" alt="attachment" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`,
        );
        popup.document.close();
      }
      return;
    } catch {
      // Fall through to anchor download attempt.
    }
  }

  window.open(url, "_blank", "noopener,noreferrer");
}
